import React, { useState } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import api from "../../services/api"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../ui/card"
import { Button } from "../ui/button"
import { Input } from "../ui/input"
import { Switch } from "../ui/switch"
import { Skeleton } from "../ui/skeleton"
import { DeleteConfirmDialog } from "../ConfirmDialog"
import { Package, Plus, X } from "lucide-react"

function ProductConfigPanel() {
  const queryClient = useQueryClient()
  const [newProductId, setNewProductId] = useState("")
  const [newProductName, setNewProductName] = useState("")
  const [deleteProduct, setDeleteProduct] = useState(null)

  // Fetch product config
  const { data, isLoading } = useQuery({
    queryKey: ["product-config"],
    queryFn: () => api.getProductConfig(),
  })

  const products = data?.products || []
  const version = data?.version || "1.0.0"
  const updatedAt = data?.updatedAt

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ products, version }) =>
      api.updateProductConfig(products, version),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product-config"] })
    },
    onError: (error) => {
      toast.error(`Failed to save: ${error.message}`)
    },
  })

  const handleToggleActive = (productId) => {
    const updatedProducts = products.map((p) =>
      p.id === productId ? { ...p, active: !p.active } : p
    )
    updateMutation.mutate({ products: updatedProducts, version })
    toast.success("Product updated")
  }

  const handleAddProduct = (e) => {
    e.preventDefault()
    if (!newProductId.trim()) return

    if (products.some((p) => p.id === newProductId.trim())) {
      toast.error("Product ID already exists")
      return
    }

    const updatedProducts = [
      ...products,
      {
        id: newProductId.trim(),
        name: newProductName.trim() || newProductId.trim(),
        active: true,
      },
    ]

    updateMutation.mutate(
      { products: updatedProducts, version },
      {
        onSuccess: () => {
          setNewProductId("")
          setNewProductName("")
          toast.success("Product added successfully")
        },
      }
    )
  }

  const handleRemoveProduct = () => {
    if (!deleteProduct) return
    const updatedProducts = products.filter((p) => p.id !== deleteProduct.id)
    updateMutation.mutate(
      { products: updatedProducts, version },
      {
        onSuccess: () => {
          setDeleteProduct(null)
          toast.success("Product removed")
        },
      }
    )
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Product Configuration
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package className="h-5 w-5" />
          Product Configuration
        </CardTitle>
        <CardDescription>
          Manage StoreKit product IDs served to the iOS app. Changes take effect
          immediately.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3 mb-4">
          {products.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No products configured
            </p>
          ) : (
            products.map((product) => (
              <div
                key={product.id}
                className={`flex items-center justify-between p-3 rounded-lg border ${
                  !product.active ? "opacity-50 bg-muted" : "bg-card"
                }`}
              >
                <div className="min-w-0 flex-1">
                  <span className="font-mono text-sm">{product.id}</span>
                  {product.name && product.name !== product.id && (
                    <span className="ml-2 text-sm text-muted-foreground">
                      {product.name}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={product.active}
                    onCheckedChange={() => handleToggleActive(product.id)}
                    disabled={updateMutation.isPending}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDeleteProduct(product)}
                    disabled={updateMutation.isPending}
                    className="text-destructive hover:text-destructive"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>

        <form onSubmit={handleAddProduct} className="flex gap-2">
          <Input
            type="text"
            value={newProductId}
            onChange={(e) => setNewProductId(e.target.value)}
            placeholder="Product ID (e.g., Annual)"
            disabled={updateMutation.isPending}
          />
          <Input
            type="text"
            value={newProductName}
            onChange={(e) => setNewProductName(e.target.value)}
            placeholder="Display name (optional)"
            disabled={updateMutation.isPending}
          />
          <Button
            type="submit"
            disabled={updateMutation.isPending || !newProductId.trim()}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Product
          </Button>
        </form>

        {updatedAt && (
          <p className="text-xs text-muted-foreground mt-4">
            Version: <code className="bg-muted px-1 rounded">{version}</code> ·
            Last updated: {new Date(updatedAt).toLocaleString()}
          </p>
        )}
      </CardContent>

      {/* Delete Confirmation */}
      <DeleteConfirmDialog
        title="Remove this product?"
        description={`This won't affect existing subscriptions, but "${deleteProduct?.id}" will no longer be offered to new users.`}
        onConfirm={handleRemoveProduct}
        open={!!deleteProduct}
        onOpenChange={(open) => !open && setDeleteProduct(null)}
      />
    </Card>
  )
}

export default ProductConfigPanel
